/**
 * Cancel Run Dialog
 *
 * @module features/releases/components/CancelRunDialog
 * @description Confirmation dialog for canceling an in-progress release run,
 * shared by `TimelineRunEntry`'s band menu and `ReleaseRunPaneHeader`'s
 * detail-pane action — both trigger the same mutation and toast copy, so the
 * confirmation itself lives here once.
 */

import { Button, Dialog, DialogFooter, DialogTrigger, Flex, Text, useToast } from '@chatsdet/ui';
import { useCancelReleaseRun } from '../useReleases';

export function CancelRunDialog({
  runId,
  isOpen,
  onOpenChange,
}: {
  runId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const cancelMutation = useCancelReleaseRun();

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span style={{ display: 'none' }} />
      <Dialog title="Cancel Run">
        <Text>Are you sure you want to cancel this run? This will abort all running jobs.</Text>
        <DialogFooter>
          <Flex gap="sm">
            <Button variant="outline" onPress={() => onOpenChange(false)}>
              Keep Running
            </Button>
            <Button
              variant="destructive"
              onPress={() => {
                cancelMutation.mutate(runId, {
                  onSuccess: () => {
                    onOpenChange(false);
                    toast.success('Run canceled');
                  },
                  onError: (err) => {
                    toast.error(err.message || 'Failed to cancel run');
                  },
                });
              }}
              isDisabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Canceling...' : 'Cancel Run'}
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
